import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface FocusModeContextValue {
  focusMode: boolean;
  setFocusMode: (v: boolean) => void;
  toggle: () => void;
}

const FocusModeContext = createContext<FocusModeContextValue | null>(null);

const STORAGE_KEY = 'pm:focus-mode';

export const FocusModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [focusMode, setFocusModeState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setFocusMode = useCallback((v: boolean) => {
    setFocusModeState(v);
    try { localStorage.setItem(STORAGE_KEY, v ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => setFocusMode(!focusMode), [focusMode, setFocusMode]);

  // F key shortcut (when no input focused)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isField = target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
      if (isField || target?.isContentEditable) return;
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setFocusMode(!focusMode);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusMode, setFocusMode]);

  return (
    <FocusModeContext.Provider value={{ focusMode, setFocusMode, toggle }}>
      {children}
    </FocusModeContext.Provider>
  );
};

export const useFocusMode = (): FocusModeContextValue => {
  const ctx = useContext(FocusModeContext);
  if (!ctx) return { focusMode: false, setFocusMode: () => {}, toggle: () => {} };
  return ctx;
};
